import dataBase from '@/dataBase/database'
import { reportService } from '@/_services'
import { fileService } from './file.service'
import { motivationalReportService } from './motivationalReport.service'
import { authentication } from '../_store/authentication.module'
import { connectWebSocket } from './websocket'

class SyncManager {
  constructor() {
    this._store = null
    this._progress = this._emptyProgress()
  }

  _emptyProgress() {
    return {
      done: 0,
      total: 0,
      report: { done: 0, total: 0 },
      reportData: { done: 0, total: 0 },
    }
  }

  _emitProgress() {
    const p = this._progress
    this._store.commit('pointreport/setSyncProgress', {
      done: p.done,
      total: p.total,
      report: { ...p.report },
      reportData: { ...p.reportData },
    })
  }

  init(store) {
    this._store = store
    this.resetPendingPhotos()

    const handleOnline = async () => {
      this._store.dispatch('user/loadUser').then(data => {
        const offlineMode = localStorage.getItem('offlineMode')
        if (data.offlineMode != offlineMode) {
          this._store.dispatch('user/changeMode', offlineMode != null ? offlineMode : false)
        }
      })
      if (authentication.state.grants.length) {
        connectWebSocket(localStorage.getItem('LoginName'))
      }
      window.dispatchEvent(new CustomEvent('app:online-sync'))
    }

    const handleOffline = () => {
      localStorage.setItem('offlineMode', true)
      this._store.commit('user/setOfflineMode', true)
      window.dispatchEvent(new CustomEvent('app:online-sync'))
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    if (navigator.onLine) {
      handleOnline()
    } else {
      handleOffline()
    }
  }

  async resetPendingPhotos() {
    const pending = await dataBase.photos.where('synced').anyOf([4, 3]).toArray()
    for (const photo of pending) {
      await dataBase.photos.update(photo.id, { synced: 0, offline: true })
    }
    console.log(`[SyncManager] Сброшено ${pending.length} зависших фото`)
  }

  async syncAll() {
    if (!navigator.onLine) return
    const t0 = performance.now()
    console.log('[SyncManager] syncAll start')
    this._store.commit('pointreport/setLoadingAsync', true)
    try {
      const pointReports = await dataBase.pointReport.where('synced').equals(0).toArray()
      const reportDataItems = await dataBase.productReportData.where('synced').equals(0).toArray()
      const totalDmpReports = await dataBase.motivationalDmpReport.where('synced').equals(0).count()
      const unsyncedMotivationalDmp = await dataBase.motivationalDmp.where('synced').equals(0).count()

      if (!reportDataItems.length && !pointReports.length && !totalDmpReports && !unsyncedMotivationalDmp) return

      // сколько несинхронизированных фото у каждого элемента
      const countPhotos = (reportId) =>
        dataBase.photos.where('reportId').equals(String(reportId)).filter(p => p.synced === 0).count()

      const photoCounts = new Map()
      for (const rd of reportDataItems) photoCounts.set(`rd:${rd.id}`, await countPhotos(rd.id))
      for (const pr of pointReports) photoCounts.set(`pr:${pr.pointId}`, await countPhotos(pr.pointId))

      this._progress = {
        done: 0,
        total: pointReports.length + reportDataItems.length,
        report: { done: 0, total: pointReports.length },
        reportData: { done: 0, total: reportDataItems.length },
      }
      this._emitProgress()

      await this._syncDmpReports()
      await this._syncMotivationalDmp()
      await this._syncReportData(photoCounts)
      await this._syncPointReports(photoCounts)

      const p = this._progress
      p.done = p.total
      p.report.done = p.report.total
      p.reportData.done = p.reportData.total
      this._emitProgress()
    } catch (err) {
      console.error('[SyncManager]', err)
    } finally {
      console.log(`[SyncManager] syncAll done in ${(performance.now() - t0).toFixed(0)} ms`)
      this._store.commit('pointreport/setLoadingAsync', false)
    }
  }

  async _syncReportData(photoCounts) {
    const progress = this._progress
    const unsynced = await dataBase.productReportData.where('synced').equals(0).toArray()

    for (const item of unsynced) {
      const reportId = String(item.id)
      const photoCount = photoCounts.get(`rd:${item.id}`) || 0
      const weight = photoCount ? 1 / photoCount : 0
      const before = progress.done
      try {
        const uploadedPhotos = await this._uploadPhotos(reportId, weight)
        await reportService.sendReportData({
          projectId: item.projectId,
          tradePointId: item.pointId,
          productId: item.productId,
          reportItemInfo: item.data,
          comment: item.data.comment,
          presense: item.data.presense,
          sync: true,
          frontPhotos: photoCount,
          photos: uploadedPhotos,
        })

        const photos = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4).toArray()
        for (const photo of photos) {
          await dataBase.photos.update(photo.id, { offline: false, synced: 1 })
        }
        await dataBase.productReportData.update(item.id, { synced: 1 })
      } catch (err) {
        const photosToReset = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4 || p.synced === 3).toArray()
        for (const photo of photosToReset) {
          await dataBase.photos.update(photo.id, { offline: true, synced: 0 })
        }
        console.error(`[SyncManager] Ошибка reportData ${item.id}:`, err)
      } finally {
        progress.done = before + 1
        progress.reportData.done++
        this._emitProgress()
      }
    }
  }

  async _syncPointReports(photoCounts) {
    const progress = this._progress
    const unsynced = await dataBase.pointReport.where('synced').equals(0).toArray()

    for (const item of unsynced) {
      const reportId = String(item.pointId)
      const photoCount = photoCounts.get(`pr:${item.pointId}`) || 0
      const weight = photoCount ? 1 / photoCount : 0
      const before = progress.done
      try {
        const uploadedPhotos = await this._uploadPhotos(reportId, weight)
        await reportService.sendPreReport({
          projectId: item.projectId,
          tradePointId: item.pointId,
          comment: item.comment,
          tempPhotos: uploadedPhotos,
          reportPhoto: item.reportPhoto,
          frontPhotos: photoCount,
          sync: true,
        })

        const photos = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4).toArray()
        for (const photo of photos) {
          await dataBase.photos.update(photo.id, { offline: false, synced: 1 })
        }
        await dataBase.pointReport.update(item.id, { synced: 1 })
      } catch (err) {
        const photosToReset = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4 || p.synced === 3).toArray()
        for (const photo of photosToReset) {
          await dataBase.photos.update(photo.id, { offline: true, synced: 0 })
        }
        console.error('Ошибка отправки отчёта:', err)
      } finally {
        progress.done = before + 1
        progress.report.done++
        this._emitProgress()
      }
    }
  }

  async _syncMotivationalDmp() {
    const unsynced = await dataBase.motivationalDmp.where('synced').equals(0).toArray()

    for (const dmp of unsynced) {
      try {
        let motivationalReportServerId
        const motivationalReport = await dataBase.motivationalReport.get(Number(dmp.localMotivationalReportId))
        if (!motivationalReport.serverId) {
          const motivationalReportData = await motivationalReportService.loadMyMotivation(
            motivationalReport.projectId,
            motivationalReport.networkId,
            motivationalReport.pointId
          )
          motivationalReportServerId = motivationalReportData.id
          // ключ — локальный id отчёта, serverId — id с сервера
          await dataBase.motivationalReport.update(motivationalReport.id, {
            serverId: motivationalReportServerId,
            synced: 1,
          })
        } else {
          motivationalReportServerId = motivationalReport.serverId
        }

        const data = await motivationalReportService.createDmp(
          { id: dmp.serverId, dmpCategory: dmp.dmpCategory, comment: dmp.comment },
          motivationalReportServerId
        )
        await dataBase.motivationalDmp.update(dmp.id, { serverId: data.id, synced: 1 })
      } catch (error) {
        console.error(`[SyncManager] Ошибка motivationalDmp ${dmp.id}:`, error)
      }
    }
  }

  async _syncDmpReports() {
    const unsynced = await dataBase.motivationalDmpReport.where('synced').equals(0).toArray()

    for (const report of unsynced) {
      let reportId = null
      try {
        const dmp = await dataBase.motivationalDmp.get(Number(report.localDmpId))
        reportId = `${dmp.localMotivationalReportId}_${dmp.id}`

        let serverDmpId
        if (dmp.serverId) {
          serverDmpId = dmp.serverId
        } else {
          let motivationalReportServerId
          const motivationalReport = await dataBase.motivationalReport.get(Number(dmp.localMotivationalReportId))
          if (!motivationalReport.serverId) {
            const reportData = await motivationalReportService.loadMyMotivation(
              motivationalReport.projectId,
              motivationalReport.networkId,
              motivationalReport.pointId
            )
            motivationalReportServerId = reportData.id
            await dataBase.motivationalReport.update(motivationalReport.id, {
              serverId: motivationalReportServerId,
              synced: 1,
            })
          } else {
            motivationalReportServerId = motivationalReport.serverId
          }

          const data = await motivationalReportService.createDmp(
            { id: dmp.serverId, dmpCategory: dmp.dmpCategory, comment: dmp.comment },
            motivationalReportServerId
          )
          serverDmpId = data.id
          await dataBase.motivationalDmp.update(dmp.id, { serverId: serverDmpId, synced: 1 })
        }

        const uploadedPhotos = await this._uploadPhotos(reportId)
        await motivationalReportService.sendReportDmp(serverDmpId, {
          dmpCategory: report.dmpCategory,
          reportPhoto: uploadedPhotos,
          sync: true,
        })

        const photos = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4).toArray()
        for (const photo of photos) {
          await dataBase.photos.update(photo.id, { offline: false, synced: 1 })
        }
        await dataBase.motivationalDmpReport.update(report.id, { synced: 1 })
      } catch (err) {
        if (reportId) {
          const photosToReset = await dataBase.photos.where('reportId').equals(reportId).filter(p => p.synced === 4 || p.synced === 3).toArray()
          for (const photo of photosToReset) {
            await dataBase.photos.update(photo.id, { offline: true, synced: 0 })
          }
        }
        console.error(`[SyncManager] Ошибка motivationalDmpReport ${report.id}:`, err)
      }
    }
  }

  async _uploadPhotos(reportId, weightPerPhoto, concurrency = 5) {
    const photos = await dataBase.photos.where('reportId').equals(String(reportId)).filter(p => p.synced === 0).toArray()

    const uploaded = []
    const duplicates = []
    const uploadedSlots = new Array(photos.length)

    const worker = async (photo, index) => {
      try {
        const formData = new FormData()
        const blob = new Blob([photo.blob], { type: `image/${photo.extension}` })
        formData.append('files[]', blob, photo.fileName)

        const data = await fileService.uploadFiles(
          formData,
          photo.motivational ? true : false,
          photo.dmp && photo.motivational ? true : false,
          () => {}
        )

        for (const ph of data) {
          ph.dmp = photo.dmp
          if (ph.alreadyLoaded) {
            duplicates.push({
              uuid: ph.uuid,
              extension: ph.extension,
            })
            await dataBase.photos.update(photo.id, { offline: false, synced: 3 })
          } else {
            await dataBase.photos.update(photo.id, { offline: false, synced: 4 })
          }
        }

        if (weightPerPhoto) {
          this._progress.done += weightPerPhoto
          this._emitProgress()
        }

        uploadedSlots[index] = data
      } catch (err) {
        console.error(`[SyncManager] Ошибка загрузки фото ${photo.id}:`, err)
      }
    }

    const queue = photos.map((photo, index) => ({ photo, index }))
    const runnersCount = Math.min(concurrency, queue.length)
    const runners = []
    for (let i = 0; i < runnersCount; i++) {
      runners.push((async () => {
        while (queue.length) {
          const task = queue.shift()
          if (!task) break
          await worker(task.photo, task.index)
        }
      })())
    }
    await Promise.all(runners)
    for (const slot of uploadedSlots) {
      if (slot) uploaded.push(...slot)
    }
    this._store.commit('pointreport/setDuplicatesPhoto', duplicates)
    return uploaded
  }
}

export default new SyncManager()